import type { ReactNode } from 'react';
import {
    CellAvatar,
    SolidStatusPill,
    type DataTableColumn,
    type DataTableColumnBase
} from '@odo/shared-ui';
import type { IteroOrder, IteroPatient } from '../../../../services/iteroBrowse';
import { BROWSE_FORMATTERS as FORMAT } from './browseFormatters';
import { TABLE_CELL_PADDING, TABLE_HEADER_CLASS } from './browserStyles';
import type { TableLayout } from './paneVisibility';
import { NEVER_SCANNED, formatFullName, formatPatientName, genderLabel } from './iteroBrowseFormat';
import type { PatientSortKey } from './patientSort';

// Plain functions, not components, so this file keeps a clean react-refresh boundary.

const BASE = {
    cellClassName: `${TABLE_CELL_PADDING} text-[13px] text-gray-700`,
    headerClassName: TABLE_HEADER_CLASS,
    truncate: true
} as const;

function muted(text: string): ReactNode {
    return <span className="text-gray-400">{text}</span>;
}

function stacked(primary: ReactNode, secondary: string | null, primaryClass = ''): ReactNode {
    return (
        <span className="flex min-w-0 flex-col leading-tight">
            <span className={`truncate ${primaryClass}`}>{primary}</span>
            {secondary && (
                <span className="mt-0.5 truncate text-[11px] text-gray-400">{secondary}</span>
            )}
        </span>
    );
}

function patientCell(lastName: string, firstName: string, chartNumber: string): ReactNode {
    return stacked(
        formatPatientName(lastName, firstName),
        chartNumber.trim() || null,
        'font-semibold text-gray-900'
    );
}

function avatarPatientCell(patient: IteroPatient): ReactNode {
    return (
        <span className="flex min-w-0 items-center gap-2.5">
            <CellAvatar name={formatFullName(patient.firstName, patient.lastName)} />
            {patientCell(patient.lastName, patient.firstName, patient.chartNumber)}
        </span>
    );
}

const patientTitle = (lastName: string, firstName: string, chartNumber: string) =>
    [formatPatientName(lastName, firstName), chartNumber.trim()].filter(Boolean).join('\n');

type PatientColumnKey = 'patient' | 'dob' | 'lastScan' | 'created' | 'gender';
type PatientColumn = DataTableColumn<IteroPatient, PatientSortKey>;
type PatientCell = Pick<DataTableColumnBase<IteroPatient>, 'label' | 'render' | 'getTitle'> &
    ({ sortable?: false } | { sortable: true; sortKey: PatientSortKey });

const PATIENT_CELLS: Record<PatientColumnKey, PatientCell> = {
    patient: {
        label: 'Patient',
        sortable: true,
        sortKey: 'LastName',
        render: avatarPatientCell,
        getTitle: (patient) =>
            patientTitle(patient.lastName, patient.firstName, patient.chartNumber)
    },
    dob: {
        label: 'Date of birth',
        render: (patient) => FORMAT.dateOnly(patient.dateOfBirth) || muted('Not recorded')
    },
    lastScan: {
        label: 'Last scan',
        sortable: true,
        sortKey: 'LastScanDate',
        render: (patient) => FORMAT.date(patient.lastScanDate) || muted(NEVER_SCANNED)
    },
    created: {
        label: 'In iTero since',
        render: (patient) => FORMAT.date(patient.creationDate) || muted('Not recorded')
    },
    gender: {
        label: 'Gender',
        render: (patient) => genderLabel(patient.gender) ?? muted('Not recorded')
    }
};

// Without the details pane the table has room for the iTero creation date; beside it, it would crowd.
const PATIENT_LAYOUTS: Record<TableLayout, readonly (readonly [PatientColumnKey, string])[]> = {
    full: [
        ['patient', '30%'],
        ['dob', '17%'],
        ['lastScan', '18%'],
        ['created', '18%'],
        ['gender', '17%']
    ],
    split: [
        ['patient', '40%'],
        ['dob', '21%'],
        ['lastScan', '21%'],
        ['gender', '18%']
    ]
};

const PATIENT_SKELETON_WIDTHS: Record<PatientColumnKey, string> = {
    patient: 'w-36',
    dob: 'w-20',
    lastScan: 'w-20',
    created: 'w-20',
    gender: 'w-14'
};

export function buildPatientColumns(layout: TableLayout): PatientColumn[] {
    return PATIENT_LAYOUTS[layout].map(([key, width]) => ({
        ...BASE,
        ...PATIENT_CELLS[key],
        key,
        width
    }));
}

export function patientSkeletonWidths(layout: TableLayout): string[] {
    return PATIENT_LAYOUTS[layout].map(([key]) => PATIENT_SKELETON_WIDTHS[key]);
}

function scannedCell(order: IteroOrder): ReactNode {
    const date = FORMAT.date(order.scanningDate);
    if (!date) return muted('Not recorded');
    return stacked(date, FORMAT.time(order.scanningDate) || null, 'font-medium text-gray-900');
}

function procedureCell(order: IteroOrder): ReactNode {
    const name = order.procedureName ?? order.procedureTypeName;
    if (!name) return muted('Not recorded');
    return stacked(name, order.procedureName ? order.procedureTypeName : null);
}

export function buildOrderColumns(): DataTableColumn<IteroOrder>[] {
    return [
        {
            ...BASE,
            key: 'scanned',
            label: 'Scanned',
            width: '17%',
            render: scannedCell
        },
        {
            ...BASE,
            key: 'patient',
            label: 'Patient',
            width: '26%',
            render: (order) =>
                patientCell(
                    order.patientLastName,
                    order.patientFirstName,
                    order.patientChartNumber
                ),
            getTitle: (order) =>
                patientTitle(
                    order.patientLastName,
                    order.patientFirstName,
                    order.patientChartNumber
                )
        },
        {
            ...BASE,
            key: 'procedure',
            label: 'Procedure',
            width: '21%',
            render: procedureCell,
            getTitle: (order) =>
                [order.procedureName, order.procedureTypeName].filter(Boolean).join('\n') ||
                undefined
        },
        {
            ...BASE,
            truncate: false,
            key: 'status',
            label: 'Status',
            width: '18%',
            render: (order) => <SolidStatusPill value={order.status} className="max-w-full" />
        },
        {
            ...BASE,
            key: 'doctor',
            label: 'Doctor',
            width: '18%',
            render: (order) => order.doctorName || muted('Not recorded'),
            getTitle: (order) => order.doctorName ?? undefined
        }
    ];
}
